import { __awaiter } from 'tslib';
import { ethers } from 'ethers';
import { DynamicError } from '../../../../errors.js';
import { logger } from '../../../../utils/logger.js';
import { assignConfirmationScreenToProvider } from '../../../../utils/assignConfirmationScreenToProvider/assignConfirmationScreenToProvider.js';
import { CancellablePromise } from '../../../../utils/CancellablePromise/CancellablePromise.js';
import { MagicClientNetworkHandler } from '../MagicClientNetworkHandler/MagicClientNetworkHandler.js';

const storedAddressKey = 'dynamic_magic_address';
class MagicWalletConnector extends MagicClientNetworkHandler {
    constructor({ apiKey, walletUiUtils, evmNetworks, config, }) {
        super({
            apiKey,
            config,
            evmNetworks,
        });
        this.key = 'magiclink';
        this.canConnectViaCustodialService = true;
        this.canConnectViaQrCode = false;
        this.connectedChain = 'EVM';
        this.supportedChains = ['ETH', 'EVM'];
        this.isWalletConnect = false;
        this.name = 'Magic Link';
        this.providerResources = undefined;
        this._walletUiUtils = walletUiUtils;
    }
    getDeepLink() {
        return undefined;
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fetchPublicAddress();
        });
    }
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            localStorage.removeItem(storedAddressKey);
            const client = this.getClient();
            yield client.user.logout();
        });
    }
    getUserMetadata() {
        return __awaiter(this, void 0, void 0, function* () {
            if (yield this._isLoggedIn()) {
                const client = this.getClient();
                return client.user.getMetadata();
            }
            return undefined;
        });
    }
    fetchPublicAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            const address = localStorage.getItem(storedAddressKey);
            if (address)
                return address;
            const client = this.getClient();
            try {
                try {
                    this._currentAuthCancellablePromise = CancellablePromise.fromPromise(this.loginWithMagic());
                    yield this._currentAuthCancellablePromise;
                }
                catch (err) {
                    logger.error(err);
                    return;
                }
                const metadata = yield client.user.getMetadata();
                if (!metadata || !metadata.publicAddress) {
                    throw new Error('No public address');
                }
                if (metadata.issuer) {
                    this.providerResources = [`magic-auth:issuer:${metadata.issuer}`];
                }
                localStorage.setItem(storedAddressKey, metadata.publicAddress);
                return metadata.publicAddress;
            }
            catch (err) {
                return undefined;
            }
        });
    }
    getBalance() {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = yield this.getWeb3Provider();
            if (!provider)
                throw new DynamicError('No provider');
            const signer = provider.getSigner();
            const userAddress = yield signer.getAddress();
            return ethers.utils.formatEther(yield provider.getBalance(userAddress));
        });
    }
    getConnectedAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            const address = localStorage.getItem(storedAddressKey);
            if (!address)
                return [];
            return [address];
        });
    }
    getNetwork() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.networkId;
        });
    }
    getRpcProvider() {
        return __awaiter(this, void 0, void 0, function* () {
            return undefined;
        });
    }
    getSigner() {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = yield this.getWeb3Provider();
            return provider.getSigner();
        });
    }
    getWeb3Provider() {
        return __awaiter(this, void 0, void 0, function* () {
            const client = this.getClient();
            const provider = new ethers.providers.Web3Provider(client.rpcProvider);
            assignConfirmationScreenToProvider(provider, this._walletUiUtils);
            return provider;
        });
    }
    isInstalledOnBrowser() {
        return false;
    }
    proveOwnership(messageToSign) {
        return this.signMessage(messageToSign);
    }
    setupEventListeners(listeners) {
        this._listeners = listeners;
        this._checkIfLoggedIn();
    }
    onChainChanged(networkChainId) {
        var _a, _b;
        (_b = (_a = this._listeners) === null || _a === void 0 ? void 0 : _a.onChainChange) === null || _b === void 0 ? void 0 : _b.call(_a, networkChainId.toString());
    }
    signMessage(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = yield this.getWeb3Provider();
            return provider.getSigner().signMessage(messageToSign);
        });
    }
    supportsNetworkSwitching() {
        return true;
    }
    switchNetwork({ networkChainId, }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!networkChainId) {
                return;
            }
            this.selectNetwork(networkChainId);
        });
    }
    teardownEventListeners() {
        this._listeners = undefined;
    }
    _isLoggedIn() {
        return __awaiter(this, void 0, void 0, function* () {
            const client = this.getClient();
            return client.user.isLoggedIn();
        });
    }
    _checkIfLoggedIn() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const isLoggedIn = yield this._isLoggedIn();
            if (!isLoggedIn) {
                localStorage.removeItem(storedAddressKey);
                (_b = (_a = this._listeners) === null || _a === void 0 ? void 0 : _a.onDisconnect) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
            return isLoggedIn;
        });
    }
}

export { MagicWalletConnector };
